// Shared IST date formatting — store UTC, render IST (UTC+05:30).
//
// DISPLAY FORMAT: "15 Aug-2026 01:10 PM" (and "15 Aug-2026" for date-only).
//
// This supersedes the older DD-MM-YYYY HH:mm convention still written in
// CLAUDE.md. The reason for the change is ambiguity: "15-08-2027" reads as
// either day-month or month-day depending on who is looking at it, and the
// portal is used on shared devices by people who have seen both. A spelled
// month cannot be misread. 12-hour time with AM/PM matches how exam timings
// are quoted to Indian students everywhere else.
//
// Every date on screen goes through this module. Raw toLocaleString() renders
// in the browser's own locale and timezone, which is how the app drifted off
// the convention before — a student in one timezone saw a different closing
// time from the one the paper actually closes at.

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    // "short" gives Jan…Dec. Deliberately not "long": a card showing
    // "15 September-2026 01:10 PM" wraps on a phone.
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
});

const DATE_ONLY_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric"
});

type Parts = Record<string, string>;

const partsOf = (formatter: Intl.DateTimeFormat, date: Date): Parts =>
    formatter.formatToParts(date).reduce<Parts>((acc, part) => {
        // Newer ICU spells September "Sept" in en-GB while every other month is
        // three letters, so dates in one column came out different widths
        // ("04 Sept-2026" beside "23 Aug-2026"). Every en-GB short month begins
        // with its three-letter form, so trimming is exact.
        acc[part.type] = part.type === "month" ? part.value.slice(0, 3) : part.value;
        return acc;
    }, {});

/** Null for anything we cannot render, so each caller renders its own dash. */
const toDate = (value: string | number | Date | null | undefined): Date | null => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** Formats a UTC timestamp as "15 Aug-2026 01:10 PM" in IST. "—" if unusable. */
export const formatIST = (value: string | number | Date | null | undefined): string => {
    const date = toDate(value);
    if (!date) {
        return "—";
    }
    const parts = partsOf(DATE_TIME_FORMATTER, date);
    // `dayPeriod` is "am"/"pm" in some ICU builds and "AM"/"PM" in others;
    // upper-casing here means the app does not depend on which one shipped.
    const meridiem = (parts.dayPeriod || "").toUpperCase();
    return `${parts.day} ${parts.month}-${parts.year} ${parts.hour}:${parts.minute} ${meridiem}`.trim();
};

/** Formats a UTC timestamp as "15 Aug-2026" (date only) in IST. "—" if unusable. */
export const formatISTDate = (value: string | number | Date | null | undefined): string => {
    const date = toDate(value);
    if (!date) {
        return "—";
    }
    const parts = partsOf(DATE_ONLY_FORMATTER, date);
    return `${parts.day} ${parts.month}-${parts.year}`;
};

/** Clock only — "01:10 PM" in IST. For rows where the date is already stated. */
export const formatISTTime = (value: string | number | Date | null | undefined): string => {
    const date = toDate(value);
    if (!date) {
        return "—";
    }
    const parts = partsOf(DATE_TIME_FORMATTER, date);
    return `${parts.hour}:${parts.minute} ${(parts.dayPeriod || "").toUpperCase()}`.trim();
};
